import { useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { apiErr } from "@/lib/format";
import { toast } from "sonner";
import { DownloadSimpleIcon, UploadSimpleIcon, FileXlsIcon, CheckCircleIcon, WarningIcon } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import HistoricalReconcile from "@/pages/_import/HistoricalReconcile";

export default function ClientImport() {
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const { user } = useAuth();

  const downloadTemplate = async () => {
    try {
      const resp = await api.get("/clients/import/template", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "plantilla_clientes_agroport.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Plantilla descargada");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const onUpload = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Selecciona un archivo"); return; }
    setUploading(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/clients/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(data.message);
    } catch (er) { toast.error(apiErr(er)); }
    finally { setUploading(false); }
  };

  return (
    <Layout
      title="Importar clientes"
      subtitle="Carga masiva desde Excel — útil para migración inicial e ingreso de datos históricos."
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="bg-card border border-border/60 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-3">
            <DownloadSimpleIcon size={20} className="text-agro-green" />
            <h3 className="font-heading font-semibold">1. Descargar plantilla</h3>
          </div>
          <p className="text-sm text-text-secondary mb-4">
            La plantilla incluye todas las columnas necesarias y una fila de ejemplo. Respeta los nombres de columnas — no los modifiques.
          </p>
          <button onClick={downloadTemplate} data-testid="download-template-btn"
            className="w-full bg-agro-green hover:bg-agro-green-hover text-black font-medium rounded-md px-4 py-2 text-sm flex items-center justify-center gap-2 active:scale-95">
            <DownloadSimpleIcon size={16} weight="bold" /> Descargar plantilla.xlsx
          </button>
          <ul className="mt-4 space-y-1.5 text-xs text-text-muted">
            <li>• Columnas con * son obligatorias: <code>codigo</code>, <code>nombre</code></li>
            <li>• <code>nivel_precio</code>: p0 / p1 / p2 / p3 / p4 (default p1)</li>
            <li>• <code>rtc_asignado_codigo</code>: usa el código del RTC (ej. RTC-001)</li>
            <li>• Si un código de cliente ya existe, se omite (no se sobrescribe)</li>
            <li>• <code>saldo_inicial</code> en Lempiras (no incluye facturas migradas)</li>
          </ul>
        </div>

        <div className="bg-card border border-border/60 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-3">
            <UploadSimpleIcon size={20} className="text-agro-yellow" />
            <h3 className="font-heading font-semibold">2. Subir archivo lleno</h3>
          </div>
          <p className="text-sm text-text-secondary mb-4">
            Llena la plantilla con tus datos y súbela aquí. El sistema valida y reporta errores línea por línea.
          </p>
          <form onSubmit={onUpload} className="space-y-3">
            <label className="block">
              <div className="bg-app border-2 border-dashed border-border hover:border-agro-yellow/40 rounded-lg p-6 text-center cursor-pointer transition-all" data-testid="upload-dropzone">
                <FileXlsIcon size={32} className="mx-auto mb-2 text-text-muted" />
                <div className="text-sm font-medium text-text-primary mb-1">
                  {file ? file.name : "Haz click o arrastra el archivo .xlsx"}
                </div>
                <div className="text-xs text-text-muted">Solo formato Excel (.xlsx)</div>
                <input type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  data-testid="upload-input"
                  onChange={(e) => { setFile(e.target.files[0]); setResult(null); }}
                  className="hidden" />
              </div>
            </label>
            <button type="submit" disabled={!file || uploading}
              data-testid="upload-submit"
              className="w-full bg-agro-yellow hover:opacity-90 text-black font-medium rounded-md px-4 py-2 text-sm flex items-center justify-center gap-2 disabled:opacity-40 active:scale-95">
              <UploadSimpleIcon size={16} weight="bold" /> {uploading ? "Importando…" : "Importar"}
            </button>
          </form>
        </div>
      </div>

      {result && (
        <div className="mt-5 bg-card border border-border/60 rounded-lg p-5" data-testid="import-result">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-agro-green/10 flex items-center justify-center">
              <CheckCircleIcon size={20} className="text-agro-green" />
            </div>
            <div>
              <div className="font-heading font-semibold">Resultado de importación</div>
              <div className="text-xs text-text-muted">{result.message}</div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="bg-agro-green/5 border border-agro-green/30 rounded-md p-3">
              <div className="text-[10px] uppercase tracking-wider text-text-muted">Importados</div>
              <div className="text-2xl font-heading font-semibold text-agro-green">{result.imported}</div>
            </div>
            <div className={`border rounded-md p-3 ${result.errors.length > 0 ? "bg-red-500/5 border-red-500/30" : "bg-app border-border"}`}>
              <div className="text-[10px] uppercase tracking-wider text-text-muted">Errores</div>
              <div className={`text-2xl font-heading font-semibold ${result.errors.length > 0 ? "text-red-400" : "text-text-muted"}`}>{result.errors.length}</div>
            </div>
          </div>
          {result.errors.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-text-muted mb-2 flex items-center gap-1.5">
                <WarningIcon size={12} className="text-agro-yellow" /> Detalle de errores
              </div>
              <div className="bg-app border border-border rounded-md overflow-hidden max-h-64 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-sidebar sticky top-0">
                    <tr className="text-left text-[10px] uppercase text-text-muted">
                      <th className="p-2 pl-3 w-16">Fila</th>
                      <th className="p-2 pr-3">Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.errors.map((e, i) => (
                      <tr key={i} className="border-t border-border/40">
                        <td className="p-2 pl-3 font-mono">{e.row}</td>
                        <td className="p-2 pr-3 text-red-400">{e.error}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Corrección N.º 2: resumen historial/CxC + conciliación (solo admin) */}
      <HistoricalReconcile isAdmin={user?.role === "admin"} refreshKey={result ? 1 : 0} />
    </Layout>
  );
}
